import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Outlet,
  createRootRoute,
  useLocation,
  useMatches,
  useNavigate,
} from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { getInterfaceLanguageCatalogue } from '@molt/shared';
import { Button } from '@/components/Button';
import { Dialog } from '@/components/Dialog';
import { IconButton } from '@/components/IconButton';
import { Header } from '@/components/shell/Header';
import { AccountMenu } from '@/components/shell/AccountMenu';
import { ShortcutsOverlay } from '@/components/shortcuts/ShortcutsOverlay';
import { WorkspaceCreateForm } from '@/components/workspace/WorkspaceCreateForm';
import { WorkspaceSwitcher } from '@/components/workspace/WorkspaceSwitcher';
import { BoardSwitcher } from '@/components/boards/BoardSwitcher';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { GlobalSearch } from '@/components/search/GlobalSearch';
import { shellClasses, shellLayoutOf } from '@/lib/shell-layout';
import { LiveRegion } from '@/components/shell/LiveRegion';
import {
  ROUTE_PENDING_MIN_MS,
  ROUTE_PENDING_MS,
  RoutePending,
} from '@/components/shell/RoutePending';
import { RouteError } from '@/components/shell/RouteError';
import { RouteNotFound } from '@/components/shell/RouteNotFound';
import {
  SIGN_IN_PATH,
  isSuperadminSession,
  signInHref,
  useSession,
  useSignOut,
} from '@/lib/session';
import { announce } from '@/lib/announce';
import { workspaceScopeFrom } from '@/lib/boards-page';
import { useNotificationLive } from '@/lib/live-notifications';
import { useCreateWorkspace, workspacesQueryOptions } from '@/lib/workspaces';
import { unreadNotificationsQueryOptions } from '@/lib/notifications';
import { refreshWebPushOnLoad } from '@/lib/web-push';
import {
  appearancePreferencesQueryOptions,
  useSetAppearanceTheme,
  useSetColourBlindMode,
} from '@/lib/appearance';
import {
  accessibilityPreferencesQueryOptions,
  useReducedMotionPreference,
  useSetReducedMotionOverride,
  useSetSingleKeyShortcutsDisabled,
} from '@/lib/accessibility-preferences';
import {
  OPEN_KEYBOARD_SHORTCUTS_EVENT,
  isEditableShortcutTarget,
  type KeyboardShortcutContext,
  type OpenKeyboardShortcutsDetail,
  platformShortcutContext,
  shortcutInventory,
} from '@/lib/keyboard-shortcuts';
import {
  applyThemeAttributes,
  mirrorAppearancePreference,
  reconcileMirroredAppearancePreference,
} from '@/lib/theme';
import {
  LocalePreferencesProvider,
  localePreferencesQueryOptions,
} from '@/lib/locale-preferences';
import {
  InterfaceLanguageProvider,
  WEB_INTERFACE_MESSAGES,
  interfaceLanguageQueryOptions,
  interfaceMessage,
  type InterfaceLanguageContextValue,
} from '@/lib/interface-language';
import { useShellChromeSuppressed } from '@/lib/shell-chrome';

/**
 * The application shell — `FE-04.11`, `entry-surfaces.md` D1.
 *
 * TanStack Router with file-based routes: a file in this directory is a route.
 * `00-overview-and-mvp-scope.md`'s cross-cutting principle 4 — every state a
 * user can reach is shareable by URL — is why the router is in the skeleton
 * rather than added when the first deep link is needed. `FR-SEARCH-012` makes
 * the board filter part of that URL, so filters belong in search params, not in
 * component state.
 *
 * `pendingComponent`/`errorComponent` here give the **root route itself** a
 * pending and an error boundary (this file has no loader today, so neither
 * fires yet, but the wiring is real and every future root-level throw is
 * caught by it). They do **not**, by themselves, cover a child route's own
 * loader: TanStack Router resolves `pendingComponent`/`errorComponent` per
 * route (`route.options.X ?? router.options.defaultX`), so a child route
 * with neither of its own falls through to `main.tsx`'s `createRouter`
 * options. ✅ **Those defaults are set** — `defaultPendingComponent`,
 * `defaultPendingMs`, `defaultPendingMinMs` and `defaultErrorComponent` are
 * wired in `main.tsx` from the same four exports used here, so a future route
 * gets both boundaries without asking for them by name. Without them a child
 * route with a loader renders NOTHING while it loads, which is `FR-EXP-034`
 * AC4's "never left ambiguous" failing on a route nobody thought to wire.
 *
 * A React error boundary (`RouteError`, via TanStack's `CatchBoundary`)
 * catches a descendant's render error regardless of how many child route
 * matches sit between it and the throw — the root's boundary already
 * protects the whole tree today, independent of the `defaultErrorComponent`
 * follow-up above, which only extends *pending* coverage the same way.
 *
 * ---------------------------------------------------------------------------
 * `D1` — THE SHELL NOW FETCHES, AND IT FETCHES EXACTLY ONE THING
 * ---------------------------------------------------------------------------
 * `RootLayout` calls `useSession()` (`GET /me`) on every route, and that is
 * the **only** request this shell makes. It carries no workspaces, no boards,
 * no notifications and no unread count — those belong to `FE-23.1`, `B17`,
 * `B19` and `B20`, and their slots stay empty until those blocks land
 * (brief §3.5).
 *
 * Two consequences worth knowing before editing this file:
 *
 *  - **Every route now issues `GET /api/me` on load, including `/sign-in` and
 *    the 404 page.** For a signed-out visitor that 401s, and that is fine:
 *    `main.tsx`'s `QueryCache.onError` excludes this query **by key**
 *    (`isSessionQuery`), so the probe cannot trigger the global redirect. If a
 *    page ever starts bouncing to `/sign-in` on its own, that exclusion is
 *    what broke — it is the fastest diagnosis available and it is written here
 *    so nobody spends an afternoon on it.
 *  - **Any test that renders this component needs a `QueryClientProvider`**
 *    (`test/query.tsx`'s `renderWithQuery`). `Header` itself still fetches
 *    nothing and still needs none, which is the presentational split holding.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE SEAM: `workspaceSwitcherSlot` IS `C9`'S, AND `D1` DELIBERATELY LEFT IT
 * ---------------------------------------------------------------------------
 * `entry-surfaces.md` **C9** records that `FR-PERM-004` AC3's *"when they
 * switch between them"* is claimed by **no `FE-` item's text** — not
 * `FE-07.1` (workspace creation), not `FE-07.2` (the workspace member list) —
 * and is "at risk of being nobody's when the queue is worked". `D1` owns "the
 * authenticated shell", which is where a switcher lives, so absorbing it here
 * would have been reasonable and is exactly what must not happen: a switcher
 * needs `GET /workspaces`, which is `FE-07.1`/`FE-23.1`'s data (chunk `D2`),
 * and a second fetch in this file would make the shell own a block's data.
 *
 * So `workspaceSwitcherSlot`, `searchSlot` and `notificationsSlot` were passed
 * nothing, on purpose, until this chunk. **`D2` extends this line; it does
 * not write a second header.** Same precedent as `C1`→`D3` on the board
 * route, which is what stopped that route being created twice.
 *
 * ⚠ `D2`'s switcher hits `AccountMenu`'s constraint verbatim: `Menu`'s
 * `MenuItem` is `onSelect`-only, so a `Link` inside an item's label works for
 * a mouse and silently does nothing for keyboard activation. `WorkspaceSwitcher`
 * takes the callback, same as `AccountMenu`'s `onOpenProfile`.
 *
 * ---------------------------------------------------------------------------
 * `D2` — THE SEAM CLOSES HERE, AND TWO MORE OPENED BEHIND IT
 * ---------------------------------------------------------------------------
 * `workspaceSwitcherSlot` is now filled with `WorkspaceSwitcher`
 * (`FE-07.3`), gated `!isSignedOut && profile !== null` so a visitor on
 * `/sign-in` neither sees it nor issues the `GET /workspaces` behind it —
 * that query would 401 and trip `main.tsx`'s redirect on the sign-in page
 * itself, `D2-BRIEF.md` §6.9 rule 2. `useWorkspaces()` (`lib/workspaces.ts`)
 * takes no options and so cannot be gated directly; this file drops to the
 * lower-level `workspacesQueryOptions()` export and calls `useQuery` itself.
 *
 * Two more contract gaps surfaced composing this, neither fixable from this
 * file (out of `D2`'s file set — both are `implementation-plan.md`
 * follow-ups, not appended here):
 *
 *  1. **`WorkspaceSwitcherProps` has no `onCloseAutoFocus` passthrough.**
 *     `Menu.tsx` (shipped, `C2`) already carries the prop and the exact
 *     precedent this needs — "a menu item that opens something you type
 *     into" — but `WorkspaceSwitcher.tsx` (agent F, wave 1) never threads it
 *     to its internal `<Menu>`, and there is no other way to reach it from
 *     outside. On the shape of defect `Menu.tsx`'s docstring names, this
 *     should mean Radix restoring focus to the (still-mounted) switcher
 *     trigger after the menu closes, on the frame after this dialog claims
 *     it — **measured, not assumed, and it does not happen**: `Dialog`'s own
 *     `FocusScope` (Radix `Dialog.Content`) traps focus inside the modal and
 *     pulls it back in the instant anything tries to move it out, so the
 *     open create-workspace dialog is a second line of defence that closes
 *     the gap `Menu`'s own missing wiring leaves. `entry-surfaces.composition
 *     .test.tsx`'s "opens the create-workspace dialog from the keyboard…"
 *     asserts focus both immediately and after a real 200ms settle. Still
 *     worth the one-line fix in `WorkspaceSwitcherProps` — a future caller of
 *     `WorkspaceSwitcher` that opens something *without* a focus-trapping
 *     `Dialog` underneath it would not have this protection — but it is not
 *     live in this chunk's own composition.
 *  2. **`WorkspaceCreateForm` forwards no ref to its name field**, so
 *     `Dialog`'s `initialFocus` — which wants a direct `RefObject` to the
 *     element — cannot be given one. This file works around it with a
 *     getter-shaped `RefObject` that queries `createFormContainerRef` for its
 *     one `<input>` at the moment Radix asks, rather than editing
 *     `WorkspaceCreateForm.tsx` (agent C, wave 1, also out of this file's set).
 */
export const Route = createRootRoute({
  component: RootLayout,
  pendingComponent: RoutePending,
  pendingMs: ROUTE_PENDING_MS,
  pendingMinMs: ROUTE_PENDING_MIN_MS,
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
});

function RootLayout() {
  /*
   * `FE-13.x` — the board face is the first surface the reading column is
   * wrong for. A route asks for the viewport with
   * `staticData: { layout: 'full-bleed' }`; a route that says nothing keeps
   * the shape `FE-04.11` shipped. The decision is pure and lives in
   * `lib/shell-layout.ts`, which carries the argument and the three classes
   * that fail silently when dropped.
   */
  const layout = shellLayoutOf(useMatches());
  const classes = shellClasses(layout);
  const embedded = layout === 'embedded';
  const shellChromeSuppressed = useShellChromeSuppressed();

  const navigate = useNavigate();
  const location = useLocation();
  const session = useSession();
  const { profile, isPending, isSignedOut } = session;
  const signOut = useSignOut();

  /*
   * `FE-07.3`'s data. `useWorkspaces()` (`lib/workspaces.ts`) takes no
   * options at all — its fixed signature is `(): UseQueryResult<...>`
   * (`D2-BRIEF.md` §6.1) — so it cannot be gated from here.
   * `workspacesQueryOptions()` is the lower-level export the same file
   * provides for exactly this: a plain options object this layout spreads
   * into its own `useQuery`, adding the `enabled` guard §6.9 rule 2
   * requires without editing a file this chunk does not own.
   *
   * The gate is not a nicety: a signed-out visitor standing on `/sign-in`
   * must not issue `GET /workspaces` at all — it would 401 and trip
   * `main.tsx`'s global redirect on the sign-in page itself, the same loop
   * `isSessionQuery` exists to keep `GET /me` out of.
   */
  const workspacesQuery = useQuery({
    ...workspacesQueryOptions(),
    enabled: !isSignedOut && profile !== null,
  });
  const unreadNotificationsQuery = useQuery({
    ...unreadNotificationsQueryOptions(),
    enabled: !isSignedOut && !isPending && profile !== null,
  });
  const appearanceQuery = useQuery({
    ...appearancePreferencesQueryOptions(),
    enabled: !isSignedOut && !isPending && profile !== null,
  });
  const accessibilityQuery = useQuery({
    ...accessibilityPreferencesQueryOptions(),
    enabled: !isSignedOut && !isPending && profile !== null,
  });
  const localeQuery = useQuery({
    ...localePreferencesQueryOptions(),
    enabled: !isSignedOut && !isPending && profile !== null,
  });
  const interfaceLanguageQuery = useQuery({
    ...interfaceLanguageQueryOptions(),
    enabled: !isSignedOut && !isPending && profile !== null,
  });
  /*
   * FE-80.1 — RESIDUAL 7, three merges old: every signed-out route can only
   * ever speak English.
   *
   * ⚠ THIS SLICE DOES NOT CLOSE IT, and the two lines below are not the fix
   * they look like. An earlier draft of this comment claimed they were; the
   * claim was refuted by reading `packages/shared`, and the refutation is
   * written out here so the next reader does not have to repeat it.
   *
   * WHY THE QUERY ABOVE STAYS GATED — this part is unchanged and load-bearing.
   * `GET /me/message-catalogue` is behind `requireUser`, so an anonymous
   * caller gets a 401 — and `isSessionQuery` (`lib/session.ts`) excludes only
   * `['me']`, a key of length 1, while this query's key
   * `['me', 'message-catalogue']` has length 2. A 401 here would therefore
   * reach `main.tsx`'s `QueryCache.onError` and navigate EVERY public route
   * (a public board, a public profile, `/sign-in` itself) to `/sign-in`.
   * Enabling it when signed out is not an option, and
   * `entry-surfaces.composition.test.tsx`'s "does not fetch the message
   * catalogue for a signed-out route" pins that.
   *
   * ⚠ WHAT THE `browserCatalogue` LINE ACTUALLY CHANGES TODAY: NOTHING
   * OBSERVABLE. `InterfaceLanguageProvider` already resolved
   * `catalogue ?? fallbackCatalogue`, where `fallbackCatalogue` is
   * `getInterfaceLanguageCatalogue(DEFAULT_INTERFACE_LANGUAGE)` at module
   * scope — so a signed-out visitor was never fed `null` and never rendered a
   * raw key. All this does is key that resolution on the BROWSER's language
   * instead of the hardcoded default. And in `packages/shared`,
   * `SUPPORTED_INTERFACE_LOCALES` has exactly ONE member, `INTERFACE_CATALOGUES`
   * exactly one entry, and `INTERFACE_LANGUAGE_ALIASES` maps `en → en-GB`, so
   * `getInterfaceLanguageCatalogue(navigator.language)` returns the same
   * `version`, `messages`, `activityTemplates`, `notificationTemplates`,
   * `resolvedLocale` and `textDirection` as the fallback for EVERY possible
   * value of `navigator.language`. Only `requestedLanguage` differs, and no
   * product file reads it.
   *
   * SO THE GATE IS NOT IN THIS FILE. It is `SUPPORTED_INTERFACE_LOCALES.length
   * === 1` together with `INTERFACE_CATALOGUES['en-GB'].messages` being `{}` —
   * both in `packages/shared`, which this slice may not touch — and the server
   * half (an anonymous `GET /message-catalogue?language=…`, since the served
   * route is caller-scoped) is unowned. **Residual 7 stays OPEN.** What these
   * two lines buy is that the day a second locale is bundled, signed-out
   * resolution follows the browser instead of needing this file edited again.
   *
   * The same expression is also the failure path for a SIGNED-IN member: if
   * the request 401s, 500s or is still in flight, `interfaceLanguageQuery.data`
   * is `undefined` and this catalogue is used instead.
   * `getInterfaceLanguageCatalogue` is total — an unrecognised, absent or
   * malformed `navigator.language` falls back to `DEFAULT_INTERFACE_LANGUAGE`
   * rather than throwing — which matters here because a throw in this
   * component's render would put `RouteError` on every route in the product,
   * including the signed-out ones.
   */
  const browserCatalogue = useMemo(
    () => getInterfaceLanguageCatalogue(window.navigator.language),
    [],
  );
  const interfaceCatalogue = interfaceLanguageQuery.data ?? browserCatalogue;
  /*
   * ⚠ `RootLayout` RENDERS `InterfaceLanguageProvider`, so it sits ABOVE its
   * own context: `useInterfaceLanguage()` here would resolve the module
   * default and speak English forever, on every route, with nothing red.
   * Hence a local translator over the same catalogue the provider is handed.
   *
   * `?? WEB_INTERFACE_MESSAGES[key]` is not a nicety. The served catalogue's
   * `messages` is `{}` today, so the bundled record is the entire resolution
   * mechanism; drop it and every key renders as its own raw key string.
   *
   * Memoised on the catalogue so the identity is stable for as long as the
   * catalogue is — the `keydown` effect and the `shortcuts` memo below both
   * declare `t`, and an unmemoised `t` would re-register the listener on
   * every render (fe-plan residual 11 is the other half: a callback closing
   * over `t` WITHOUT declaring it announces the previous language after a
   * switch).
   */
  const t = useMemo<InterfaceLanguageContextValue['t']>(
    () => (key, values, count) => {
      const message = interfaceCatalogue.messages[key] ?? WEB_INTERFACE_MESSAGES[key];
      if (message === undefined) return key;
      return interfaceMessage(message, values, count);
    },
    [interfaceCatalogue],
  );
  const setAppearanceTheme = useSetAppearanceTheme();
  const setColourBlindMode = useSetColourBlindMode();
  const setSingleKeyShortcutsDisabled = useSetSingleKeyShortcutsDisabled();
  const setReducedMotionOverride = useSetReducedMotionOverride();
  const reducedMotion = useReducedMotionPreference(accessibilityQuery.data);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [shortcutContextOverride, setShortcutContextOverride] =
    useState<KeyboardShortcutContext | null>(null);
  const [, setGoPrefix] = useState(false);
  const goPrefixRef = useRef(false);
  const [boardSwitcherOpen, setBoardSwitcherOpen] = useState(false);
  const boardSwitcherTriggerRef = useRef<HTMLButtonElement>(null);
  const shortcutReturnFocusRef = useRef<HTMLElement | null>(null);
  const accountTriggerRef = useRef<HTMLButtonElement>(null);
  const [systemDark, setSystemDark] = useState(
    () => window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false,
  );
  const shortcutPlatform = platformShortcutContext(window.navigator.userAgent);
  const defaultShortcutContext = useMemo<KeyboardShortcutContext>(
    () => ({
      canEdit: true,
      canUseCardTarget: true,
      canUseWatch: true,
      canUseDates: true,
      canUseLabels: true,
      canUseMembers: true,
      singleKeyShortcutsDisabled:
        accessibilityQuery.data?.singleKeyShortcutsDisabled ?? false,
      platform: shortcutPlatform,
    }),
    [accessibilityQuery.data?.singleKeyShortcutsDisabled, shortcutPlatform],
  );
  const shortcuts = useMemo(
    () =>
      shortcutInventory(
        shortcutContextOverride === null
          ? defaultShortcutContext
          : {
              ...shortcutContextOverride,
              singleKeyShortcutsDisabled:
                defaultShortcutContext.singleKeyShortcutsDisabled,
              platform: defaultShortcutContext.platform,
            },
        t,
      ),
    [defaultShortcutContext, shortcutContextOverride, t],
  );

  useEffect(() => {
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (media === undefined) return undefined;
    function handleChange(event: MediaQueryListEvent) {
      setSystemDark(event.matches);
    }
    media.addEventListener('change', handleChange);
    setSystemDark(media.matches);
    return () => media.removeEventListener('change', handleChange);
  }, []);

  /*
   * ── `FE-04.19` — the browser-local appearance mirror, keyed to the member ──
   *
   * ⚠ **This effect hangs off the SESSION, and the effect below it hangs off
   * the APPEARANCE QUERY. That is the whole item; do not merge them.**
   *
   * `appearanceQuery` is gated `enabled: !isSignedOut && !isPending && profile
   * !== null`, so a signed-out session never issues it and its `data` is
   * `undefined` forever. The effect below therefore returns early, and since
   * nothing else in this tree writes `data-theme`, `data-appearance-theme` or
   * `data-colour-blind-labels`, **the departed member's private theme stays
   * painted on the signed-out surface across every subsequent reload, for
   * every later visitor to that browser, with no event that ever corrects
   * it** — `molt.appearance` still holding their preference the whole time.
   * `FR-EXP-024`'s edge case is explicit that *"the theme is user-level and
   * private"*. `GET /me` RESOLVES for a signed-out visitor where the appearance
   * query does not, which is why the reconciliation is anchored here — it
   * resolves as a 401, which `api.ts` collapses to `isUnauthenticated` and
   * `useSession` reports as `isSignedOut`. That is a *resolved* fact, unlike
   * the `undefined` the appearance query is stuck at forever.
   *
   * ⚠ **Both guards below are load-bearing and neither is defensive
   * padding.** `isPending` means we do not know yet — treating "no profile
   * yet" as "signed out" is the mistake `lib/session.ts` warns about, and here
   * it would discard a valid mirror on *every* load and give up
   * `FR-EXP-024` AC4 outright. The second guard keeps a non-401 failure (the
   * network is down; `profile` is null and `isSignedOut` is false) from being
   * read as an absent member.
   *
   * What this does NOT buy is a correct first frame for a visitor nobody has
   * identified yet; `lib/theme.ts` carries that argument in full. The read
   * happens at module scope in `main.tsx` before any request has been
   * answered, so the correction necessarily lands one round trip in.
   *
   * ⚠ **This effect is a NO-OP under Vitest, so a green unit suite is not
   * evidence about it.** `window.localStorage` has no methods in that harness,
   * `readMirror` throws into its `catch` and the reconciliation returns
   * `'absent'` without touching the document — which means every component
   * test that renders this layout without `installMemoryStorage` exercises
   * nothing here. `lib/theme.test.ts` (which opts in), `e2e/FR-EXP-024.spec.ts`
   * and `e2e/fe15-appearance-mirror-per-user.capture.ts` are the coverage.
   */
  useEffect(() => {
    if (isPending) return;
    if (profile === null && !isSignedOut) return;
    reconcileMirroredAppearancePreference(profile?.id ?? null);
  }, [isPending, isSignedOut, profile]);

  useEffect(() => {
    if (appearanceQuery.data === undefined || profile === null) return;
    applyThemeAttributes(
      document.documentElement,
      appearanceQuery.data.theme,
      systemDark,
    );
    document.documentElement.dataset['colourBlindLabels'] = String(
      appearanceQuery.data.colourBlindMode,
    );
    /*
     * The re-stamp, and the reason the legacy migration costs nothing: an
     * unstamped record left in place by the effect above is overwritten with
     * an owned one the instant this member's preferences arrive.
     */
    mirrorAppearancePreference(
      appearanceQuery.data.theme,
      appearanceQuery.data.colourBlindMode,
      profile.id,
    );
  }, [appearanceQuery.data, profile, systemDark]);
  useEffect(() => {
    document.documentElement.dataset['reducedMotion'] = String(reducedMotion);
  }, [reducedMotion]);
  /*
   * Web Push (`lib/web-push.ts`, `docs/notes/pwa.md`): once per signed-in
   * member per load, re-register this device's subscription so its keys and
   * `last_seen_at` stay current. Keyed on the member id, not module scope —
   * a PUT before the session resolves is a 401. Without a worker registration
   * or a subscription (every http origin, jsdom) it makes no request at all.
   */
  const profileId = profile?.id ?? null;
  useEffect(() => {
    if (isSignedOut || isPending || profileId === null) return;
    refreshWebPushOnLoad().catch(() => {
      // Best effort: the subscription the server already holds still works.
    });
  }, [isPending, isSignedOut, profileId]);
  useEffect(() => {
    if (isSignedOut || isPending || profile === null) {
      setShortcutsOpen(false);
      setBoardSwitcherOpen(false);
    }
  }, [isPending, isSignedOut, profile]);
  useEffect(() => {
    if (isSignedOut || isPending || profile === null) {
      setGoPrefix(false);
      goPrefixRef.current = false;
      return undefined;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        isEditableShortcutTarget(event.target) ||
        defaultShortcutContext.singleKeyShortcutsDisabled
      ) {
        if (goPrefixRef.current) {
          goPrefixRef.current = false;
          setGoPrefix(false);
        }
        return;
      }

      if (!goPrefixRef.current) {
        if (event.key.toLocaleLowerCase() === 'g') {
          event.preventDefault();
          goPrefixRef.current = true;
          setGoPrefix(true);
        } else if (event.key.toLocaleLowerCase() === 'b' && canOpenBoardSwitcher(event)) {
          /*
           * `FR-EXP-017`'s `B`. Here rather than in its own listener so that an
           * armed `g` consumes the `b` like any other undefined second key —
           * two listeners would both see it. Nothing on the board face claims
           * `b` or stops propagation, so this reaches it too. `preventDefault`
           * keeps the keystroke out of the filter input that takes focus.
           */
          event.preventDefault();
          setBoardSwitcherOpen(true);
        }
        return;
      }

      goPrefixRef.current = false;
      setGoPrefix(false);
      if (event.key.toLocaleLowerCase() === 'i') {
        event.preventDefault();
        announce(t('shell.inboxOpened'));
        void navigate({ to: '/inbox' });
      } else if (event.key.toLocaleLowerCase() === 'p') {
        event.preventDefault();
        announce(t('shell.plannerOpened'));
        void navigate({ to: '/planner' });
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    defaultShortcutContext.singleKeyShortcutsDisabled,
    isPending,
    isSignedOut,
    navigate,
    profile,
    t,
  ]);
  useEffect(() => {
    if (isSignedOut || isPending || profile === null) return undefined;
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== '?') return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isEditableShortcutTarget(event.target)) return;
      if (
        event.target instanceof HTMLElement &&
        event.target.closest('[data-board-shortcut-scope]') !== null
      ) {
        return;
      }
      event.preventDefault();
      openShortcutsFromElement(document.activeElement, null);
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isPending, isSignedOut, profile]);
  useEffect(() => {
    function handleOpenShortcuts(event: Event) {
      const detail = (event as CustomEvent<OpenKeyboardShortcutsDetail>).detail ?? {};
      openShortcutsFromElement(
        detail.returnFocus ?? document.activeElement,
        detail.context,
      );
    }

    document.addEventListener(OPEN_KEYBOARD_SHORTCUTS_EVENT, handleOpenShortcuts);
    return () =>
      document.removeEventListener(OPEN_KEYBOARD_SHORTCUTS_EVENT, handleOpenShortcuts);
  }, []);
  useNotificationLive(!isSignedOut && !isPending && profile !== null);

  /**
   * `B` opens nothing when the switcher is not mounted (embedded or
   * chrome-suppressed routes), when a modal hides the header (a Radix dialog —
   * the card back among them — marks everything outside it `aria-hidden`), or
   * when focus is inside another overlay or menu, which owns its own keys.
   */
  function canOpenBoardSwitcher(event: KeyboardEvent): boolean {
    const trigger = boardSwitcherTriggerRef.current;
    if (trigger?.closest('[aria-hidden="true"]') !== null) return false;
    return !(
      event.target instanceof Element &&
      event.target.closest('[role="dialog"], [role="alertdialog"], [role="menu"]') !==
        null
    );
  }

  function openShortcutsFromElement(
    element: Element | null,
    context: KeyboardShortcutContext | null | undefined,
  ): void {
    shortcutReturnFocusRef.current = element instanceof HTMLElement ? element : null;
    setShortcutContextOverride(context ?? null);
    setShortcutsOpen(true);
  }

  function openShortcutsFromAccountMenu(): void {
    shortcutReturnFocusRef.current = accountTriggerRef.current;
    setShortcutContextOverride(null);
    setShortcutsOpen(true);
  }

  /*
   * The workspace scoped by `?workspace=` on WHATEVER route is current, not
   * only `/` — read off the raw location rather than through `useSearch()`.
   * `/` is the only route that will declare `workspace` in its own
   * `validateSearch` (agent G's file, §6.7), so trusting a typed search here
   * would tie this shell-wide layout to one route's search schema; this
   * narrows the raw string the same way every other reader of an
   * unvalidated search value does (§5.4).
   */
  const currentWorkspaceId = workspaceScopeFrom(
    new URLSearchParams(location.searchStr).get('workspace'),
  );

  const [createWorkspaceOpen, setCreateWorkspaceOpen] = useState(false);
  const createWorkspace = useCreateWorkspace();
  /*
   * `Dialog` owns initial focus here and aims it at the form's name field
   * through the ref `WorkspaceCreateForm` forwards.
   *
   * The inline host on `/` does the same job with the form's `autoFocus`
   * instead, and the split is deliberate: two things claiming initial focus
   * inside a `Dialog` is a fight, so the `Dialog` host passes this and never
   * `autoFocus`.
   */
  const nameFieldRef = useRef<HTMLInputElement>(null);

  function handleCreateWorkspace(name: string) {
    createWorkspace.mutate(name, {
      onSuccess: (created) => {
        // D11: one channel. The dialog closing is the only visible
        // consequence besides the new group appearing — see the return.
        announce(t('shellShortcuts.root.workspaceCreated', { name: created.name }));
        setCreateWorkspaceOpen(false);
      },
    });
  }

  function workspaceSwitcherSlot(): ReactNode {
    // §6.9 rule 2 — mounted only for a signed-in session, matching the
    // query's own `enabled` gate above so the two decisions cannot disagree.
    if (isSignedOut || isPending || profile === null) return undefined;

    return (
      <WorkspaceSwitcher
        workspaces={workspacesQuery.data ?? []}
        currentWorkspaceId={currentWorkspaceId}
        onSelect={(id) => {
          void navigate({ to: '/', search: (prev) => ({ ...prev, workspace: id }) });
        }}
        onOpenMembers={(workspaceId) => {
          void navigate({ to: '/w/$workspaceId/members', params: { workspaceId } });
        }}
        onOpenActivity={(workspaceId) => {
          void navigate({ to: '/w/$workspaceId/activity', params: { workspaceId } });
        }}
        onCreateWorkspace={() => {
          setCreateWorkspaceOpen(true);
        }}
      />
    );
  }

  /** The `/b/:shortLink/…` board on screen, read off the path like `currentWorkspaceId`. */
  const currentBoardShortLink = /^\/b\/([^/]+)/.exec(location.pathname)?.[1] ?? null;

  function boardSwitcherSlot(): ReactNode {
    // The same signed-in gate as the workspace switcher: `GET /boards` 401s.
    if (isSignedOut || isPending || profile === null) return undefined;

    return (
      <BoardSwitcher
        open={boardSwitcherOpen}
        onOpenChange={setBoardSwitcherOpen}
        currentBoardShortLink={currentBoardShortLink}
        triggerRef={boardSwitcherTriggerRef}
        onNavigate={(href) => {
          void navigate({ href });
        }}
      />
    );
  }

  function accountSlot(): ReactNode {
    if (isSignedOut) {
      /*
       * No "Sign in" control on `/sign-in` itself: the form is already the
       * page, and a `next` built from the current location there would nest
       * one sign-in URL inside another
       * (`/sign-in?next=%2Fsign-in%3Fnext%3D…`) — legal, useless, and the
       * kind of URL that appears in a bug report six months later.
       */
      if (location.pathname === SIGN_IN_PATH) return null;
      return (
        <Button
          as="a"
          variant="primary"
          size="sm"
          /*
           * ⚠ `{ pathname, searchStr }`, NOT `location.href`.
           * `entry-surfaces.md` D2's own worked example passes `href`, which
           * carries the origin — and `safeNextPath`, the function that reads
           * this value back on the way out, rejects anything that does not
           * begin with a single `/`. A `next` built from `href` would
           * therefore collapse EVERY redirect to `/`, silently, with the
           * member landing on the wrong page and nothing red anywhere.
           *
           * A plain anchor, so this is a document load rather than a client
           * navigation. That is deliberate: `signInHref` owns the one
           * spelling of this URL, and splitting it back into `to` + `search`
           * for a `Link` would be a second place the `next` key is written.
           * The cost is a reload for a visitor whose cache is empty anyway.
           */
          href={signInHref({ pathname: location.pathname, search: location.searchStr })}
        >
          {t('shell.signIn')}
        </Button>
      );
    }

    /*
     * `isPending` (we do not know yet) and a probe that failed for some
     * non-session reason both land here, and both render nothing — one test,
     * because the answer is the same and D12's rule is about what must NOT be
     * drawn: an avatar for a member we cannot name, or a "Sign in" for someone
     * who may well be signed in. `isPending` is destructured and read here so
     * the state is named rather than folded silently into `profile === null`.
     */
    if (isPending || profile === null) return null;

    return (
      <AccountMenu
        profile={profile}
        triggerRef={accountTriggerRef}
        accessibility={{
          preferences: accessibilityQuery.data ?? null,
          loading: accessibilityQuery.isLoading,
          singleKeyPending: setSingleKeyShortcutsDisabled.isPending,
          reducedMotionPending: setReducedMotionOverride.isPending,
          onOpenShortcuts: openShortcutsFromAccountMenu,
          onSingleKeyShortcutsDisabledChange: (disabled) =>
            setSingleKeyShortcutsDisabled.mutate(disabled),
          onReducedMotionOverrideChange: (enabled) =>
            setReducedMotionOverride.mutate(enabled),
        }}
        appearance={{
          preferences: appearanceQuery.data ?? null,
          loading: appearanceQuery.isLoading,
          themePending: setAppearanceTheme.isPending,
          colourBlindModePending: setColourBlindMode.isPending,
          onThemeChange: (theme) => setAppearanceTheme.mutate(theme),
          onColourBlindModeChange: (enabled) => setColourBlindMode.mutate(enabled),
        }}
        /*
         * `AccountMenu` is presentational and deliberately does not navigate
         * itself: `MenuItem` is `onSelect`-only, and a `Link` inside an
         * item's label works for a mouse click and silently does nothing for
         * keyboard activation (Radix calls `currentTarget.click()` on the
         * item, never on a descendant). The route supplies real navigation.
         */
        onOpenProfile={() => {
          void navigate({ to: '/profile' });
        }}
        onOpenInbox={() => {
          void navigate({ to: '/inbox' });
        }}
        onOpenPlanner={() => {
          void navigate({ to: '/planner' });
        }}
        onOpenMyCards={() => {
          void navigate({ to: '/my/cards' });
        }}
        /*
         * SA-9: the console entry exists for a platform superadmin only. A
         * normal account gets no item at all rather than a disabled one — the
         * console's existence is not advertised, as `/admin` itself is Not found.
         */
        onOpenAdmin={
          isSuperadminSession(session)
            ? () => {
                void navigate({ to: '/admin' });
              }
            : undefined
        }
        onSignOut={() => {
          /*
           * §5.3's order lives in `useSignOut`: the network call first, then
           * `queryClient.clear()` — never `invalidateQueries`, which would
           * refetch every entry the outgoing member could read and have all
           * of them 401 on the way out. If the call throws, the cache is left
           * alone and this navigation never happens, so a network blip does
           * not scrub a session that may still be live.
           */
          signOut.mutate(undefined, {
            onSuccess: () => {
              void navigate({ to: SIGN_IN_PATH, replace: true });
            },
          });
        }}
        signingOut={signOut.isPending}
      />
    );
  }

  function notificationsSlot(): ReactNode {
    if (isSignedOut || isPending || profile === null) return undefined;
    return (
      <NotificationBell
        loading={unreadNotificationsQuery.isLoading}
        {...(unreadNotificationsQuery.data === undefined
          ? {}
          : { unreadCount: unreadNotificationsQuery.data.count })}
      />
    );
  }

  function searchSlot(): ReactNode {
    if (isSignedOut || isPending || profile === null) return undefined;
    return (
      <GlobalSearch
        compact
        onNavigate={(href) => {
          void navigate({ href });
        }}
      />
    );
  }

  return (
    <InterfaceLanguageProvider catalogue={interfaceCatalogue}>
      <LocalePreferencesProvider preferences={localeQuery.data}>
        <div className={classes.root}>
          {/*
        Skip link first in the DOM: keyboard users should not have to tab
        through a header on every navigation (cross-cutting principle 6).
      */}
          {embedded ? null : (
            <a
              href="#main"
              className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded focus:bg-accent focus:px-3 focus:py-2 focus:text-accent-ink"
            >
              {t('shellShortcuts.root.skipToContent')}
            </a>
          )}

          {/*
        D11: before `<main>`, so a screen reader's virtual cursor never lands
        inside a region that mutates under it. One node, shell singleton —
        see `components/shell/LiveRegion.tsx`.
        F2 keeps it before the header too: `GlobalSearch`'s combobox owns a
        local loading status, and the singleton must remain the first polite
        live region composition tests observe.
      */}
          <LiveRegion />

          {/*
        The account slot, and the branch order is the decision (brief §5.1's
        sibling trap).

        `useSession()` can return a **non-null `profile` beside
        `isSignedOut: true`**: TanStack Query keeps the last successful `data`
        alongside a new error, which is precisely the shape of a session that
        has just expired. `isSignedOut` is therefore tested FIRST, everywhere
        in this chunk — showing an account menu for a session that no longer
        exists is a lie the member will act on (they open Profile, every save
        401s), whereas showing "Sign in" to someone whose session is genuinely
        gone is simply true.

        `isPending` renders **nothing at all** — not a placeholder avatar and
        not a "Sign in" guess. D12's whole point is that "we do not know yet"
        must not render either branch, and `Header` produces no
        `data-shell-slot` anchor for an absent slot, so an unresolved session
        leaves no dead markup behind either.
      */}
          {/*
        `workspaceSwitcherSlot` is passed PLAINLY — `HeaderProps` already
        declares it `ReactNode`, which includes `undefined`, and
        `workspaceSwitcherSlot()` returns `undefined` for a signed-out or
        still-pending session. A conditional JSX spread here would compile
        even if `Header` declared no such prop at all
        (`exactOptionalPropertyTypes` does not check spread attributes) —
        `D2-BRIEF.md` §5.1, the seam `C2` got wrong for drag-handle plumbing.
      */}
          {embedded || shellChromeSuppressed ? null : (
            <Header
              accountSlot={accountSlot()}
              workspaceSwitcherSlot={workspaceSwitcherSlot()}
              boardSwitcherSlot={boardSwitcherSlot()}
              searchSlot={searchSlot()}
              notificationsSlot={notificationsSlot()}
            />
          )}

          {/*
        `tabIndex={-1}`: without it, following the skip link moves the
        scroll position but leaves focus in the header, so the next Tab
        re-enters the nav instead of the content the member asked to skip
        to (D1).
      */}
          <main id="main" tabIndex={-1} className={classes.main}>
            <Outlet />
          </main>

          {/*
        `FE-07.3`'s "New workspace…" host — `entry-surfaces.md` D4's Dialog
        arm. The inline arm (the boards landing page's first-run empty
        state) is agent G's; this is the only other place this form mounts,
        which is why it is the same component (`WorkspaceCreateForm`'s own
        docstring) rather than a second one.
      */}
          <Dialog
            open={createWorkspaceOpen}
            onOpenChange={(open) => {
              setCreateWorkspaceOpen(open);
              if (!open) createWorkspace.reset();
            }}
            title={t('shellShortcuts.root.createWorkspaceTitle')}
            initialFocus={nameFieldRef}
            closeControl={
              <IconButton label={t('shellShortcuts.root.createWorkspaceClose')}>
                <X aria-hidden="true" className="size-4" />
              </IconButton>
            }
          >
            <WorkspaceCreateForm
              nameFieldRef={nameFieldRef}
              onSubmit={handleCreateWorkspace}
              onCancel={() => {
                setCreateWorkspaceOpen(false);
              }}
              busy={createWorkspace.isPending}
              {...(createWorkspace.error === null
                ? {}
                : { error: createWorkspace.error.message })}
            />
          </Dialog>
          <ShortcutsOverlay
            open={shortcutsOpen}
            onOpenChange={setShortcutsOpen}
            returnFocusRef={shortcutReturnFocusRef}
            shortcuts={shortcuts}
            singleKeyShortcutsDisabled={
              accessibilityQuery.data?.singleKeyShortcutsDisabled ?? false
            }
            onSingleKeyShortcutsDisabledChange={(disabled) =>
              setSingleKeyShortcutsDisabled.mutate(disabled)
            }
            preferencesBusy={
              accessibilityQuery.isLoading ||
              setSingleKeyShortcutsDisabled.isPending ||
              setReducedMotionOverride.isPending
            }
            reducedMotionOverride={
              accessibilityQuery.data?.reducedMotionOverride ?? false
            }
            onReducedMotionOverrideChange={(enabled) =>
              setReducedMotionOverride.mutate(enabled)
            }
          />
        </div>
      </LocalePreferencesProvider>
    </InterfaceLanguageProvider>
  );
}
